'use client'
import { createContext, useContext, useState, useEffect } from 'react';
import { appStorage } from '../utils/appStorage';
import {
  aiEnglishLanguageOptions,
  childEnglishLanguageOptions,
  childEnglishStandardOptions,
  courseOptions,
  flipOptions,
  intervalOptions,
  languageOptions,
  randomPlayOptions,
  speedOptions,
} from "../app/setting/options";
import logger from '../utils/logger';

const ConfigContext = createContext();

// アプリ別の設定キーを取得（sessionStorage → URLパラメータ → 旧共有キー）
function getSettingsKey() {
  if (typeof window === 'undefined') return 'smartEnglishSettings';
  const appName = appStorage.getAppName()
    || new URLSearchParams(window.location.search).get('app');
  return appName ? `${appName.toLowerCase()}_settings` : 'smartEnglishSettings';
}

export function ConfigProvider({ children }) {
  const [config, setConfig] = useState({
    course: courseOptions[0],
    courseDetail: null,
    language: languageOptions[0],
    interval: intervalOptions[0],
    flip: flipOptions[0],
    randomPlay: randomPlayOptions[0],
    speed: speedOptions[0],
    skitLanguage: childEnglishLanguageOptions[0],
  });
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const key = getSettingsKey();
    let savedConfig = localStorage.getItem(key);

    // マイグレーション: アプリ別キーがなければ旧共有キーから読み込み
    if (!savedConfig && key !== 'smartEnglishSettings') {
      savedConfig = localStorage.getItem('smartEnglishSettings');
    }

    if (savedConfig) {
      try {
        const parsedConfig = JSON.parse(savedConfig);

        const validatedConfig = {
          course: parsedConfig.course && courseOptions.includes(parsedConfig.course)
            ? parsedConfig.course : courseOptions[0],
          courseDetail: parsedConfig.courseDetail || null,
          language: parsedConfig.language && (languageOptions.includes(parsedConfig.language) || aiEnglishLanguageOptions.includes(parsedConfig.language) || childEnglishStandardOptions.includes(parsedConfig.language))
            ? parsedConfig.language : languageOptions[0],
          interval: parsedConfig.interval && intervalOptions.includes(parsedConfig.interval)
            ? parsedConfig.interval : intervalOptions[0],
          flip: parsedConfig.flip && flipOptions.includes(parsedConfig.flip)
            ? parsedConfig.flip : flipOptions[0],
          randomPlay: parsedConfig.randomPlay && randomPlayOptions.includes(parsedConfig.randomPlay)
            ? parsedConfig.randomPlay : randomPlayOptions[0],
          speed: parsedConfig.speed && speedOptions.includes(parsedConfig.speed)
            ? parsedConfig.speed : speedOptions[0],
          skitLanguage: parsedConfig.skitLanguage && childEnglishLanguageOptions.includes(parsedConfig.skitLanguage)
            ? parsedConfig.skitLanguage : childEnglishLanguageOptions[0],
        };

        setConfig(validatedConfig);
      } catch (error) {
        logger.error('Error loading saved settings:', error);
      }
    }
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (!isLoaded) return; // 初回読み込み完了前は保存しない（問題F対応）
    const key = getSettingsKey();
    localStorage.setItem(key, JSON.stringify(config));
  }, [config, isLoaded]);

  return (
    <ConfigContext.Provider value={{ config, setConfig }}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  return useContext(ConfigContext);
}
